window.AmbitionRuntime.register('ui-motion', context => {
  const page = document.querySelector('.ambition-page');
  if (!page || page.dataset.uiMotion === 'ready') return;
  page.dataset.uiMotion = 'ready';

  // Wrap only existing text. Anchors, accessible names and focus targets stay intact.
  const targets = document.querySelectorAll(
    '.ambition-page .cta, .ambition-page .ambition-header a, .ambition-page .colors button, ' +
    '#rn-footer .rn-footer__nav a, #rn-footer .rn-footer__vip-link, #rn-footer .rn-footer__local-note a'
  );
  targets.forEach(target => {
    [...target.childNodes].forEach(node => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
        const label = document.createElement('span');
        label.className = 'ui-motion-label';
        node.replaceWith(label);
        label.append(node);
      } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'SPAN') {
        node.classList.add('ui-motion-label');
      }
    });
  });

  // Pointer state gives touch/pen the same immediate response as mouse :active.
  let pressedTarget = null;
  const clearPress = () => {
    pressedTarget?.removeAttribute('data-ui-pressed');
    pressedTarget = null;
  };
  context.listen(document, 'pointerdown', event => {
    clearPress();
    if (!event.isPrimary || event.button !== 0 || event.pointerType === 'mouse') return;
    const target = event.target.closest('.ambition-page a, .ambition-page button, #rn-footer a, #rn-footer button');
    if (target) {
      pressedTarget = target;
      target.setAttribute('data-ui-pressed', '');
    }
  });
  context.listen(window, 'pointerup', clearPress);
  context.listen(window, 'pointercancel', clearPress);
  context.listen(window, 'blur', clearPress);

  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const imageFades = new Set();
  const stopImageFades = () => imageFades.forEach(animation => animation.cancel());
  context.listen(motion, 'change', () => {
    if (motion.matches) stopImageFades();
  });
  page.querySelectorAll('.film img, .editorial img, .product-image img, .accessories img, .loop-track article img').forEach(image => {
    if (image.complete || image.closest('.gallery-previous, [inert], [aria-hidden="true"]')) return;
    const originalSource = image.getAttribute('src');
    context.listen(image, 'load', async () => {
      try { await image.decode(); } catch { return; }
      if (context.signal.aborted || motion.matches || !image.isConnected || image.getAttribute('src') !== originalSource || image.closest('[aria-busy="true"]')) return;
      const animation = image.animate({ opacity: [.6, 1] }, { duration: 300, easing: 'ease-out' });
      imageFades.add(animation);
      animation.finished.then(() => imageFades.delete(animation), () => imageFades.delete(animation));
    }, { once: true });
  });

  const galleries = [...page.querySelectorAll('[data-gallery]')];
  const photos = galleries.map(gallery => gallery.querySelector('.product-image')).filter(Boolean);
  const syncLoading = gallery => {
    gallery.querySelector('.product-image')?.toggleAttribute('data-ui-loading', gallery.getAttribute('aria-busy') === 'true');
  };
  const loadingObserver = new MutationObserver(records => {
    records.forEach(record => syncLoading(record.target));
  });
  galleries.forEach(gallery => {
    syncLoading(gallery);
    loadingObserver.observe(gallery, { attributes: true, attributeFilter: ['aria-busy'] });
  });

  // A loading pulse runs only while its photo is in view and this tab is visible.
  const visiblePhotos = new Set();
  function syncVisibility() {
    photos.forEach(photo => photo.toggleAttribute('data-ui-visible', !document.hidden && visiblePhotos.has(photo)));
  }
  const visibilityObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) visiblePhotos.add(entry.target);
      else visiblePhotos.delete(entry.target);
    });
    syncVisibility();
  });
  photos.forEach(photo => visibilityObserver.observe(photo));
  context.listen(document, 'visibilitychange', syncVisibility);
  context.cleanup(() => {
    clearPress();
    loadingObserver.disconnect(); visibilityObserver.disconnect();
    stopImageFades(); imageFades.clear();
    photos.forEach(photo => { photo.removeAttribute('data-ui-loading'); photo.removeAttribute('data-ui-visible'); });
    delete page.dataset.uiMotion;
  });
});

window.AmbitionRuntime.register('navigation', context => {
  const page = document.querySelector('.ambition-page');
  const header = page?.querySelector('[data-ambition-dock]');
  const nav = header?.querySelector('nav');
  if (!nav) return;
  const root = document.documentElement;
  const editing = Boolean(window.Shopify?.designMode);
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const attributes = ['data-dock-mounted', 'data-dock-state', 'data-dock-intro', 'data-fullscreen', 'data-editing-field'];
  const previousAttributes = attributes.map(name => [name, header.getAttribute(name)]);
  const originalInert = header.inert;
  const visibility = [header.style.getPropertyValue('visibility'), header.style.getPropertyPriority('visibility')];
  const clearance = [root.style.getPropertyValue('--ambition-dock-clearance'), root.style.getPropertyPriority('--ambition-dock-clearance')];
  let savedFocus = header.contains(document.activeElement) ? document.activeElement : null;
  let anchorMarker = null, frame = 0, ready = false, shown = false, nativeFullscreen = false;
  let entrance = [], introTimer = 0, pointerTimer = 0, entranceWidth = 0, entranceShell = null;
  let active;
  const links = [...nav.querySelectorAll('a[href]')];
  const originalLinkInert = links.map(link => [link, link.inert]);
  const originalCurrent = links.map(link => [link, link.getAttribute('aria-current')]);
  const here = new URL(location.href);
  const chapters = links.slice(1).filter(link => !link.hasAttribute('data-ambition-dock-cta')).flatMap(link => {
    try {
      const url = new URL(link.getAttribute('href'), here);
      if (url.origin !== here.origin || url.pathname !== here.pathname || url.search !== here.search || !url.hash || url.hash === '#inicio') return [];
      const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
      return target ? [{ link, target }] : [];
    } catch { return []; }
  });

  header.setAttribute('data-dock-mounted', '');
  header.dataset.dockState = 'waiting';
  header.style.visibility = 'hidden'; header.inert = true;
  page.classList.add('has-floating-navigation');
  if (!editing) {
    anchorMarker = document.createComment('ambition navigation');
    header.before(anchorMarker);
    document.body.insertBefore(header, page.parentNode === document.body ? page : document.body.firstChild);
  }
  function cancelEntrance() {
    clearTimeout(introTimer); clearTimeout(pointerTimer);
    const running = entrance; entrance = [];
    running.forEach(animation => animation.cancel());
    entranceShell?.remove(); entranceShell = null;
    delete header.dataset.dockIntro;
    originalLinkInert.forEach(([link, inert]) => { link.inert = inert; });
  }
  function playEntrance() {
    const box = nav.getBoundingClientRect();
    const logo = links[0]?.getBoundingClientRect();
    if (!logo || !box.width) return;
    const style = getComputedStyle(nav);
    const padding = ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth'].reduce((sum, property) => sum + (parseFloat(style[property]) || 0), 0);
    const inset = Math.max(0, (box.width - logo.width - padding) / 2);
    const compact = `inset(0px ${inset}px 0px ${inset}px round 999px)`;
    const expanded = 'inset(0px 0px 0px 0px round 999px)';
    const ease = 'cubic-bezier(.22,.61,.36,1)';
    const options = { duration: 1000, easing: 'linear', fill: 'both' };
    const centered = rect => box.width / 2 - (rect.left - box.left + rect.width / 2);
    entranceWidth = nav.offsetWidth;
    entranceShell = document.createElement('span');
    entranceShell.className = 'ambition-dock__entrance-shell';
    entranceShell.setAttribute('aria-hidden', 'true');
    Object.assign(entranceShell.style, {
      background: style.backgroundColor, border: style.border,
      borderRadius: style.borderRadius, boxShadow: style.boxShadow,
      backdropFilter: style.backdropFilter, webkitBackdropFilter: style.webkitBackdropFilter
    });
    nav.append(entranceShell);
    header.dataset.dockIntro = 'logo';
    links.slice(1).forEach(link => { link.inert = true; });
    const shell = entranceShell.animate([
      { opacity: 0, clipPath: compact, transform: 'translateY(8px)', offset: 0, easing: ease },
      { opacity: 1, clipPath: compact, transform: 'translateY(0px)', offset: .18 },
      { opacity: 1, clipPath: compact, transform: 'translateY(0px)', offset: .36, easing: ease },
      { opacity: 1, clipPath: expanded, transform: 'translateY(0px)', offset: 1 }
    ], options);
    const logoMotion = links[0].animate([
      { opacity: 0, transform: `translate(${centered(logo)}px, 4px)`, offset: 0, easing: ease },
      { opacity: 1, transform: `translate(${centered(logo)}px, 0px)`, offset: .18 },
      { opacity: 1, transform: `translate(${centered(logo)}px, 0px)`, offset: .36, easing: ease },
      { opacity: 1, transform: 'translate(0px, 0px)', offset: 1 }
    ], options);
    entrance = [shell, logoMotion, ...links.slice(1).map((link, index) => {
      const offset = centered(link.getBoundingClientRect());
      return link.animate([
        { opacity: 0, transform: `translateX(${offset}px)`, offset: 0, easing: ease },
        { opacity: 0, transform: 'translateX(0px)', offset: .65, easing: ease },
        { opacity: 1, transform: 'translateX(0px)', offset: 1 }
      ], { duration: 380, delay: 430 + index * 80, easing: 'linear', fill: 'both' });
    })];
    introTimer = setTimeout(() => { if (entrance.includes(shell)) header.dataset.dockIntro = 'opening'; }, 360);
    shell.finished.then(() => { if (entrance.includes(shell)) { cancelEntrance(); schedule(); } }, () => {});
  }
  function available() {
    return ready && !document.hidden && !document.fullscreenElement && !document.webkitFullscreenElement && !nativeFullscreen && !document.querySelector('[data-scroll-cinema-active="true"]') && !isField();
  }
  function isField() {
    return document.activeElement?.matches?.('input:not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="color"]), textarea, select, [contenteditable="true"]');
  }
  function syncAvailability() {
    if (context.signal.aborted) return;
    const visible = available();
    if (!visible && header.contains(document.activeElement)) savedFocus = document.activeElement;
    header.dataset.dockState = ready ? 'ready' : 'waiting';
    header.dataset.fullscreen = String(Boolean(document.fullscreenElement || document.webkitFullscreenElement || nativeFullscreen));
    header.dataset.editingField = String(Boolean(isField()));
    header.style.visibility = visible ? 'visible' : 'hidden';
    header.inert = originalInert || !visible;
    if (!visible) cancelEntrance();
    if (visible && savedFocus?.isConnected && document.activeElement === document.body) savedFocus.focus({ preventScroll: true });
    if (visible) savedFocus = null;
  }
  function update() {
    frame = 0;
    if (context.signal.aborted || document.hidden) return;
    if (entrance.length && nav.offsetWidth !== entranceWidth) cancelEntrance();
    const height = nav.getBoundingClientRect().height;
    const bottom = Number.parseFloat(getComputedStyle(header).bottom) || 0;
    const value = `${Math.ceil(height + Math.max(0, bottom) + 16)}px`;
    if (height > 0 && root.style.getPropertyValue('--ambition-dock-clearance') !== value) {
      root.style.setProperty('--ambition-dock-clearance', value);
      document.dispatchEvent(new CustomEvent('ambition:dock-resize', { detail: { clearance: Number.parseFloat(value) } }));
    }
    const line = innerHeight * .35;
    const positions = chapters.map(chapter => ({ ...chapter, top: chapter.target.getBoundingClientRect().top })).sort((a, b) => a.top - b.top);
    let next = null;
    positions.forEach(chapter => { if (chapter.top <= line) next = chapter.link; });
    if (next !== active) {
      active = next;
      chapters.forEach(({ link }) => { if (link === active) link.setAttribute('aria-current', 'location'); else link.removeAttribute('aria-current'); });
    }
  }
  function schedule() { if (!context.signal.aborted && !frame) frame = requestAnimationFrame(update); }
  function show(animate) {
    if (context.signal.aborted) return;
    ready = true; syncAvailability(); schedule();
    if (!available() || shown) return;
    shown = true;
    const replay = page.dataset.ambitionDockShown;
    page.dataset.ambitionDockShown = 'true';
    if (animate && !editing && !replay && !motion.matches && nav.animate && CSS.supports('clip-path', 'inset(0 round 999px)')) playEntrance();
  }
  const sizeObserver = new ResizeObserver(schedule);
  sizeObserver.observe(nav); sizeObserver.observe(page);
  const chapterObserver = new IntersectionObserver(schedule, { threshold: [0, .01, .5] });
  chapters.forEach(chapter => chapterObserver.observe(chapter.target));
  context.listen(window, 'scroll', schedule, { passive: true });
  context.listen(window, 'resize', () => { cancelEntrance(); schedule(); });
  context.listen(window.visualViewport, 'resize', schedule);
  context.listen(window, 'hashchange', schedule);
  context.listen(window, 'pageshow', event => { if (event.persisted) { cancelEntrance(); show(false); } schedule(); });
  context.listen(document, 'fullscreenchange', syncAvailability);
  context.listen(document, 'webkitfullscreenchange', syncAvailability);
  context.listen(document, 'ambition:cinema-state', syncAvailability);
  context.listen(document, 'webkitbeginfullscreen', () => { nativeFullscreen = true; syncAvailability(); }, { capture: true });
  context.listen(document, 'webkitendfullscreen', () => { nativeFullscreen = false; syncAvailability(); }, { capture: true });
  context.listen(document, 'focusin', syncAvailability);
  context.listen(document, 'focusout', () => queueMicrotask(syncAvailability));
  context.listen(document, 'keydown', event => { if (event.key === 'Tab') cancelEntrance(); });
  // Hold the moving hitbox until the native click has completed.
  context.listen(header, 'pointerdown', () => {
    entrance.forEach(animation => {
      const time = animation.currentTime;
      animation.pause();
      if (time !== null) animation.currentTime = time;
    });
  });
  context.listen(window, 'pointerup', () => { if (entrance.length) pointerTimer = setTimeout(cancelEntrance, 240); });
  context.listen(window, 'pointercancel', cancelEntrance);
  context.listen(window, 'blur', cancelEntrance);
  context.listen(header, 'click', () => { if (entrance.length) requestAnimationFrame(() => { if (!context.signal.aborted) cancelEntrance(); }); });
  context.listen(document, 'visibilitychange', () => {
    if (document.hidden) { cancelAnimationFrame(frame); frame = 0; cancelEntrance(); syncAvailability(); }
    else { syncAvailability(); if (ready) show(false); schedule(); }
  });
  context.listen(motion, 'change', () => { if (motion.matches) cancelEntrance(); syncAvailability(); schedule(); });
  document.fonts.ready.then(schedule);
  context.cleanup(() => {
    cancelAnimationFrame(frame); cancelEntrance(); sizeObserver.disconnect(); chapterObserver.disconnect();
    const focused = header.contains(document.activeElement) ? document.activeElement : null;
    originalCurrent.forEach(([link, value]) => { if (value === null) link.removeAttribute('aria-current'); else link.setAttribute('aria-current', value); });
    previousAttributes.forEach(([name, value]) => { if (value === null) header.removeAttribute(name); else header.setAttribute(name, value); });
    if (visibility[0]) header.style.setProperty('visibility', visibility[0], visibility[1]); else header.style.removeProperty('visibility');
    header.inert = originalInert;
    if (anchorMarker?.parentNode) anchorMarker.replaceWith(header);
    else if (anchorMarker) header.remove();
    page.classList.remove('has-floating-navigation');
    if (clearance[0]) root.style.setProperty('--ambition-dock-clearance', clearance[0], clearance[1]); else root.style.removeProperty('--ambition-dock-clearance');
    if (focused?.isConnected && document.activeElement === document.body && !header.inert) focused.focus({ preventScroll: true });
  });
  schedule();
  if (window.AmbitionLoading?.ready) window.AmbitionLoading.ready.then(() => show(true));
  else show(true);
});
